/**
 * Search Module  
 * 搜索模块
 * 
 * @description 处理预测搜索和搜索功能
 * @version 1.0.0
 */

const SearchModule = (function() {
  'use strict';

  let searchCache = {};

  /**
   * 执行预测搜索
   * @param {string} query - 搜索关键词
   * @returns {Promise<Object>} 搜索结果
   */
  async function predictiveSearch(query) {
    // 检查缓存
    if (searchCache[query]) {
      return searchCache[query];
    }

    try {
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`
      );

      if (!response.ok) {
        throw new Error('Search request failed');
      }

      const data = await response.json();
      
      // 缓存结果
      searchCache[query] = data;
      
      return data;
    } catch (error) {
      console.error('Predictive search error:', error);
      throw error;
    }
  }

  /**
   * 初始化搜索模块
   */
  function init() {
    const searchInputs = document.querySelectorAll('[data-search-input]');

    searchInputs.forEach(input => {
      const resultsContainer = input.parentElement.querySelector('[data-search-results]');
      if (!resultsContainer) return;

      // 防抖处理搜索
      input.addEventListener('input', DealerThemeUtils.debounce(async (e) => {
        const query = e.target.value.trim();

        if (query.length < 2) {
          resultsContainer.innerHTML = '';
          resultsContainer.classList.remove('is-visible');
          return;
        }

        try {
          const results = await predictiveSearch(query);
          displayResults(results, resultsContainer, query);
        } catch (error) {
          resultsContainer.innerHTML = '<div class="search-error">搜索出错，请稍后再试</div>';
        }
      }, 300));

      // 点击外部关闭搜索结果
      document.addEventListener('click', (e) => {
        if (!input.contains(e.target) && !resultsContainer.contains(e.target)) {
          resultsContainer.classList.remove('is-visible');
        }
      });
    });

    console.log('🔍 Search module initialized');
  }

  /**
   * 显示搜索结果
   * @param {Object} results - 搜索结果数据
   * @param {Element} container - 结果容器
   * @param {string} query - 搜索关键词
   */
  function displayResults(results, container, query) {
    if (!results.resources || !results.resources.results) {
      container.innerHTML = '<div class="search-no-results">未找到相关结果</div>';
      container.classList.add('is-visible');
      return;
    }

    const products = results.resources.results.products || [];

    if (products.length === 0) {
      container.innerHTML = '<div class="search-no-results">未找到相关产品</div>';
      container.classList.add('is-visible');
      return;
    }

    let html = '<div class="search-results">';
    
    products.forEach(product => {
      html += `
        <a href="${product.url}" class="search-result-item">
          ${product.featured_image ? `
            <div class="search-result-item__image">
              <img src="${product.featured_image}" alt="${product.title}" loading="lazy">
            </div>
          ` : ''}
          <div class="search-result-item__info">
            <h4 class="search-result-item__title">${highlightQuery(product.title, query)}</h4>
            <div class="search-result-item__price">
              ${getPriceHTML(product)}
            </div>
          </div>
        </a>
      `;
    });

    html += `<a href="/search?q=${encodeURIComponent(query)}" class="search-view-all">查看所有结果</a>`;
    html += '</div>';

    container.innerHTML = html;
    container.classList.add('is-visible');
  }

  /**
   * 高亮搜索关键词
   * @param {string} text - 原文本
   * @param {string} query - 搜索关键词
   * @returns {string} 高亮后的 HTML
   */
  function highlightQuery(text, query) {
    const regex = new RegExp(`(${query})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  }

  /**
   * 获取价格 HTML
   * @param {Object} product - 产品数据
   * @returns {string} 价格 HTML
   */
  function getPriceHTML(product) {
    const isDealer = DealerThemeUtils.isDealerUser();
    
    if (isDealer && product.price) {
      const price = DealerThemeUtils.formatMoney(product.price, window.Shopify.money_format);
      return `<span class="price">${price}</span>`;
    } else {
      const placeholder = window.themeSettings?.pricePlaceholder || '?';
      return `<span class="price price--hidden">${placeholder}</span>`;
    }
  }

  // 返回公开 API
  return {
    init,
    predictiveSearch
  };
})();

// DOM 加载完成后初始化
document.addEventListener('DOMContentLoaded', () => {
  SearchModule.init();
});

// 暴露到全局
window.SearchModule = SearchModule;

